/**
 * Single source of truth for managed external API providers.
 * Drives:
 *   - server proxy env-var lookup (envVarName)
 *   - per-user personal-key fallback (userKeyField on UserApiKeys)
 *   - "Add my key" / "Notify admins" flow on the Rerun Gaps step
 *   - probe verification when a user supplies a personal key
 *
 * Keep entries minimal — UI text lives in components, not here.
 */
import type { UserApiKeys } from '../types/siteRag';

export type ApiProviderId = 'gemini' | 'nrel-solar' | 'nrel-wind' | 'eia' | 'airnow';

export interface ApiProviderDef {
  id: ApiProviderId;
  displayName: string;
  /** Env var the server proxy reads (matches server.ts SERVER_API_KEYS). */
  envVarName: string;
  /** Field on the UserApiKeys store slice that holds the user's personal key. */
  userKeyField: keyof UserApiKeys;
  /** Free-tier signup URL — opened in a new tab from the gap UI. */
  signupUrl: string;
  /** Optional API docs link. */
  docsUrl?: string;
  /** Lightweight endpoint hit to verify a key works. No real query. */
  probeUrl: string;
  probeMethod: 'GET';
  /** Query-param name that carries the api key (varies by provider). */
  probeQueryParam: string;
  /** Fixed query params required by the probe (e.g. lat/lng for solar). */
  probeFixedParams?: Record<string, string>;
  /**
   * Substrings in a gap reason that route classification to this
   * provider. DEPRECATED: prefer the structured `providerIds` exact-
   * match path below — Content-Source Fixes Prompt 10. Retained as a
   * fallback for legacy gap objects that don't yet carry a
   * `providerId` field.
   */
  gapMatchKeys: string[];
  /**
   * Canonical provider ids that route to this auth-provider entry.
   * Used for exact `providerId === id` matching when the gap object
   * carries a structured `providerId` (preferred path post-Prompt 10).
   * Typically `[ this.id ]`, but kept as a list because one auth
   * entry can serve multiple feeds (e.g. NREL key covers solar + wind).
   */
  providerIds: string[];
  /** Who can manage this key. 'org' = admin only. */
  scope: 'user' | 'org' | 'both';
}

export const API_PROVIDERS: Record<ApiProviderId, ApiProviderDef> = {
  gemini: {
    id: 'gemini',
    displayName: 'Google Gemini',
    envVarName: 'GEMINI_API_KEY',
    userKeyField: 'geminiKey',
    signupUrl: 'https://aistudio.google.com/apikey',
    docsUrl: 'https://ai.google.dev/gemini-api/docs',
    probeUrl: 'https://generativelanguage.googleapis.com/v1beta/models',
    probeMethod: 'GET',
    probeQueryParam: 'key',
    gapMatchKeys: ['Gemini', 'gemini', 'Site-Specific Research', 'AI narrative'],
    providerIds: ['gemini'],
    scope: 'both',
  },
  'nrel-solar': {
    id: 'nrel-solar',
    displayName: 'NREL Solar Resource API',
    envVarName: 'NREL_API_KEY',
    userKeyField: 'nrelKey',
    signupUrl: 'https://developer.nrel.gov/signup/',
    docsUrl: 'https://developer.nrel.gov/docs/solar/solar-resource-v1/',
    probeUrl: 'https://developer.nrel.gov/api/solar/solar_resource/v1.json',
    probeMethod: 'GET',
    probeQueryParam: 'api_key',
    probeFixedParams: { lat: '40.0', lon: '-105.0' },
    gapMatchKeys: ['NREL Solar', 'NREL Solar Resource'],
    providerIds: ['nrel-solar'],
    scope: 'both',
  },
  'nrel-wind': {
    id: 'nrel-wind',
    displayName: 'NREL Wind Resource API',
    envVarName: 'NREL_API_KEY',
    userKeyField: 'nrelKey',
    signupUrl: 'https://developer.nrel.gov/signup/',
    probeUrl: 'https://developer.nrel.gov/api/utility_rates/v3.json',
    probeMethod: 'GET',
    probeQueryParam: 'api_key',
    probeFixedParams: { lat: '40.0', lon: '-105.0' },
    gapMatchKeys: ['NREL Wind'],
    providerIds: ['nrel-wind'],
    scope: 'both',
  },
  eia: {
    id: 'eia',
    displayName: 'EIA Electricity Retail Sales',
    envVarName: 'EIA_API_KEY',
    userKeyField: 'eiaKey',
    signupUrl: 'https://www.eia.gov/opendata/register.php',
    probeUrl: 'https://api.eia.gov/v2/electricity',
    probeMethod: 'GET',
    probeQueryParam: 'api_key',
    gapMatchKeys: ['EIA Electricity', 'EIA Retail', 'EIA:', 'EIA Natural Gas'],
    // v1.2.1 HR-5 P2-4 — the natural-gas provider reuses the SAME EIA account /
    // key, so it shares the registry entry (gap-classification + personal-key
    // routing recognise 'eia-natural-gas' without a new secret).
    providerIds: ['eia', 'eia-electricity', 'eia-natural-gas'],
    scope: 'both',
  },
  airnow: {
    id: 'airnow',
    displayName: 'AirNow EPA Real-Time AQI',
    envVarName: 'AIRNOW_API_KEY',
    userKeyField: 'airnowKey',
    signupUrl: 'https://docs.airnowapi.org/account/request/',
    probeUrl: 'https://www.airnowapi.org/aq/observation/current/ziplatlong/',
    probeMethod: 'GET',
    probeQueryParam: 'API_KEY',
    probeFixedParams: { format: 'application/json', zipcode: '20002' },
    gapMatchKeys: ['AirNow', 'AirNow EPA'],
    providerIds: ['airnow'],
    scope: 'both',
  },
};

/** Classification result for a SED gap or sanity finding. */
export type GapClassification =
  | { kind: 'apiKey'; provider: ApiProviderDef; httpStatus: number | null }
  | { kind: 'document' }
  | { kind: 'crossValidation' }
  | { kind: 'unknown' };

const KEY_PATTERNS: Array<{ rx: RegExp; status: number }> = [
  { rx: /\b401\b/, status: 401 },
  { rx: /\b403\b/, status: 403 },
  { rx: /Unauthorized/i, status: 401 },
  { rx: /Forbidden/i, status: 403 },
  { rx: /API.?KEY.?(invalid|missing|forbidden)/i, status: 403 },
  { rx: /API key not configured/i, status: 403 },
];

/**
 * Classify a gap/finding into one of:
 *   - apiKey      → user can supply a personal key or notify admins
 *   - document    → user can upload a doc / paste a value / mark not relevant
 *   - crossValidation → human verification step
 *   - unknown     → fall back to generic options
 *
 * Accepts either a bare reason string (legacy callers) or a
 * structured `{ reason, providerId? }` (preferred post-Content-Source
 * Fixes Prompt 10). When `providerId` is supplied, the provider lookup
 * is an exact match against `providerIds` — the substring scan of the
 * reason text is only used when no providerId is available.
 */
export function classifyGap(input: string | { reason: string; providerId?: string }): GapClassification {
  const reason = typeof input === 'string' ? (input ?? '') : (input.reason ?? '');
  const providerId = typeof input === 'string' ? undefined : input.providerId;
  const keyHit = KEY_PATTERNS.find(p => p.rx.test(reason));
  if (keyHit) {
    let provider: ApiProviderDef | undefined;
    if (providerId) {
      // Structured exact match — preferred path. Skips the brittle
      // substring scan entirely.
      provider = Object.values(API_PROVIDERS).find(p => p.providerIds.includes(providerId));
    }
    if (!provider) {
      // Legacy fallback: substring match against the human-readable
      // reason. Triggers when the caller hasn't migrated to passing
      // providerId yet, or when the gap originated outside the
      // structured assessGaps path.
      const lower = reason.toLowerCase();
      provider = Object.values(API_PROVIDERS).find(p =>
        p.gapMatchKeys.some(k => lower.includes(k.toLowerCase())),
      );
    }
    if (provider) return { kind: 'apiKey', provider, httpStatus: keyHit.status };
  }
  if (/cross.?validation|verify/i.test(reason)) return { kind: 'crossValidation' };
  if (/not retrieved|no data|missing data|parcel data/i.test(reason)) return { kind: 'document' };
  return { kind: 'unknown' };
}

/* ── P3-8 — provider source-of-truth cross-reference ────────────────────────
 *
 * Three registries describe providers, each for a different scope:
 *   1. `API_PROVIDERS` (this file)         — managed AUTH-KEY providers (probe,
 *                                            user-key field, gap classification).
 *   2. `PROVIDER_CATALOG` (dataProviders)  — the FULL feed catalogue (UI,
 *                                            recommendation engine).
 *   3. `SERVER_PROXY_KEY_CONFIG` (types)   — host → {param, env} for the proxy.
 * They were never cross-checked, so a key provider could exist here with no
 * catalogue entry, or vice-versa, drifting silently.
 *
 * Rather than physically merge three differently-shaped registries (which would
 * risk the deployed proxy), this helper is the SSOT RECONCILER: given the
 * catalogue's provider ids, it reports any `API_PROVIDERS` `providerIds` that
 * have NO catalogue entry. A unit test (`apiProviders.ssot.test.ts`) calls this
 * with the real `PROVIDER_CATALOG` ids and asserts zero drift, so the three
 * cannot diverge again. Pure / dependency-free (catalogue ids passed in to
 * avoid an import cycle config ← services). */
/**
 * Auth providers that legitimately have NO `PROVIDER_CATALOG` feed entry: the
 * Gemini LLM is the AI-narrative key, not a SED DATA feed (the catalogue only
 * lists site-evidence data providers), so it is exempt from the reconciler.
 */
const NON_CATALOG_AUTH_PROVIDERS: ReadonlySet<ApiProviderId> = new Set(['gemini']);

export function findProviderRegistryDrift(
  catalogProviderIds: readonly string[],
): { id: ApiProviderId; missingProviderIds: string[] }[] {
  const catalog = new Set(catalogProviderIds);
  // Catalogue ids use hyphenated feed names (e.g. 'eia-electricity',
  // 'airnow-aqi'); auth `providerIds` use the short auth id ('eia', 'airnow').
  // A provider is reconciled when ANY catalogue id starts with the short id.
  const drift: { id: ApiProviderId; missingProviderIds: string[] }[] = [];
  for (const def of Object.values(API_PROVIDERS)) {
    if (NON_CATALOG_AUTH_PROVIDERS.has(def.id)) continue;
    const missing = def.providerIds.filter(
      pid => !catalog.has(pid) && ![...catalog].some(cid => cid === pid || cid.startsWith(`${pid}-`)),
    );
    if (missing.length > 0) drift.push({ id: def.id, missingProviderIds: missing });
  }
  return drift;
}
